import React, { useState } from 'react';
import { Auth, API } from 'aws-amplify';
import { useTranslation } from 'react-i18next';
import { generateAdminEmailsForAllAccountTypes } from '../login/components/tempEmail.helper';
import { createAccountAndUser } from '../../state/helperServices/auth/createAccountAndUser.query';
import { AccountTypes } from '../../components/changeUser/accountTypes.enum';
import { Role } from '../../state/reduxRepositories/users/users.interfaces';
import { isValidEmail } from '../../helpers/emailValidator';
import { Spinner } from '../../components/spinner/spinner';

interface DevSignUpProps {
  accountExists: (email: string) => void;
}

export function DevSignUp({ accountExists }: DevSignUpProps) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  return (
    <>
      {isLoading ? (
        <Spinner open={isLoading} />
      ) : (
        <div>
          <form
            onSubmit={async (event) => {
              event.preventDefault();
              setIsLoading(true);
              await checkIfAccountsExistAndCreateIfNot(email, accountExists);
              setIsLoading(false);
            }}
          >
            <input
              type="text"
              name="email"
              placeholder={t('ENTER_EMAIL') || ''}
              onChange={(event) => setEmail(event.target.value.toLowerCase())}
              value={email}
            />
            <button type="submit">{t('SIGN_UP')}</button>
          </form>
        </div>
      )}
    </>
  );
}

async function checkIfAccountsExistAndCreateIfNot(
  email: string,
  accountExists: (email: string) => void,
) {
  // Using signIn to test if email exists. Because signUp causes an email limit error after a few time in personal accounts.
  try {
    const accountAdmins = generateAdminEmailsForAllAccountTypes(
      email.toLowerCase(),
    );

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    await Auth.signIn(
      accountAdmins.guardianAccount,
      accountAdmins.guardianAccount,
    );

    await Auth.signOut();
    accountExists(email.toLowerCase());
  } catch (error) {
    signUpAllAccounts(email.toLowerCase(), accountExists);
  }
}

const getNameFromEmailForDev = (email: string) => {
  const name = email.split('+');
  const lastName = name[1].split('@');
  return { firstName: name[0], lastName: lastName[0] };
};

const signUpAllAccounts = (
  _email: string,
  accountExists: (email: string) => void,
) => {
  if (!isValidEmail(_email)) {
    console.error('email not valid');
    return;
  }
  // TODO: remove once email limit is fixed and uncomment code below.
  // accountExists(_email);

  const accountAdmins = generateAdminEmailsForAllAccountTypes(_email);

  const orgAccount = signUp(AccountTypes.Org, accountAdmins.orgAccount);
  const clinicAccount = signUp(
    AccountTypes.Clinic,
    accountAdmins.clinicAccount,
  );
  const guardianAccount = signUpGuardian(accountAdmins.guardianAccount);

  Promise.all([orgAccount, clinicAccount, guardianAccount])
    .then((values) => {
      // console.log(values);
      accountExists(_email);
    })
    .catch((error) => {
      // make sure this doesn't send email (I got a LimitExceededException: ..daily limit for the operation or the account... Amazon SES configuration for sending email)
      if (error.code === 'UsernameExistsException') {
        accountExists(_email);
        return;
      }
      // if (error.message === 'User already exists') {
      //   accountExists(_email);
      // }
      if (
        error?.errors[0]?.message ===
        'Creating account or user failed. Error: Creating cognito user failed. UsernameExistsException: User account already exists'
      ) {
        accountExists(_email);
      }
      console.error(error);
    });
};

async function signUpGuardian(_email: string) {
  return Auth.signUp({
    username: _email,
    password: _email,
    attributes: {
      email: _email,
      given_name: getNameFromEmailForDev(_email).firstName,
      family_name: getNameFromEmailForDev(_email).lastName,
      'custom:postalCode': '3000',
      // phoneNumber,
    },
    // autoSignIn: {
    //   // optional - enables auto sign in after user is confirmed
    //   enabled: true,
    // },
  });
}

async function signUp(accountType: AccountTypes, email: string) {
  return API.graphql({
    query: createAccountAndUser,
    variables: {
      input: {
        account: {
          type: accountType,
          // TODO: add name.
          name: email,
          email,
        },
        user: {
          firstName: getNameFromEmailForDev(email).firstName,
          lastName: getNameFromEmailForDev(email).lastName,
          email,
          role: [Role.ADMIN],
          enabled: true,
        },
      },
    },
    authMode: 'API_KEY',
  });

  // return API.graphql(
  //   graphqlOperation(createAccountAndUser, {
  //     input: {
  //       account: {
  //         type: accountType,
  //       },
  //       user: {
  //         email,
  //       },
  //     },
  //   }),
  // );
}
